<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";
import { addSizeToDataLayer } from "@/functions/analytics";
import DimensionInput from "@/components/UI/DimensionInput.vue";
import ShapeInputFieldsWrapper from "@/components/UI/ShapeInputFieldsWrapper.vue";
import {Props} from "@/components/ShapeType.vue";
import ValidationMessage from '@/components/UI/ValidationMessage.vue';

const store = useStore();
const { isCutout, action, prefix } = defineProps<Props>();

const dimensions = computed(() => store.getters.getMeasurements('jewel', isCutout));
const dimensionLabel = computed(() => store.getters.dimensionLabel);
const dimensionMaxLabel = computed(() => store.getters.getMaxLabelForDimension);
const isDimensionValid = computed(() => store.getters.isShapeValid(isCutout));
const message = computed(() => store.getters.getInstructionsForDimension);

const widthTop = computed({
	get: () => dimensions.value.width_top,
	set: (value: string) => {
		store.dispatch(action, {
			dimension: "width_top",
			value: value,
		});
	},
});

const widthBottom = computed({
	get: () => dimensions.value.width_bottom,
	set: (value: string) => {
		store.dispatch(action, {
			dimension: "width_bottom",
			value: value,
		});
	},
});

const heightTop = computed({
	get: () => dimensions.value.height_top,
	set: (value: string) => {
		store.dispatch(action, {
			dimension: "height_top",
			value: value,
		});
	},
});

const heightBottom = computed({
	get: () => dimensions.value.height_bottom,
	set: (value: string) => {
		store.dispatch(action, {
			dimension: "height_bottom",
			value: value,
		});
	},
});

</script>
<template>
	<shape-input-fields-wrapper>
		<dimension-input
			inputRef="jewel-height_top"
			:id="`${prefix}measurements-jewel-height_top`"
			:name="`${prefix}measurements[dimensions][height_top]`"
			unit="cm"
			:label="dimensionLabel('height_top')"
			:instructions="isCutout ? '' : dimensionMaxLabel('height_top')"
			:invalid="!isDimensionValid(`${prefix}jewel-height_top`)"
			:onBlur="() => addSizeToDataLayer('height_top', +heightTop, 'jewel')"
			v-model="heightTop"
		/>
		<dimension-input
			inputRef="jewel-height_bottom"
			:id="`${prefix}measurements-jewel-height_bottom`"
			:name="`${prefix}measurements[dimensions][height_bottom]`"
			unit="cm"
			:label="dimensionLabel('height_bottom')"
			:instructions="isCutout ? '' : dimensionMaxLabel('height_bottom')"
			:invalid="!isDimensionValid(`${prefix}jewel-height_bottom`)"
			:onBlur="() => addSizeToDataLayer('height_bottom', +heightBottom, 'jewel')"
			v-model="heightBottom"
		/>
		<dimension-input
			inputRef="jewel-width_top"
			:id="`${prefix}measurements-jewel-width_top`"
			:name="`${prefix}measurements[dimensions][width_top]`"
			unit="cm"
			:label="dimensionLabel('width_top')"
			:instructions="isCutout ? '' : dimensionMaxLabel('width_top')"
			:invalid="!isDimensionValid(`${prefix}jewel-width_top`)"
			:onBlur="() => addSizeToDataLayer('width_top', +widthTop, 'jewel')"
			v-model="widthTop"
		/>
		<dimension-input
			inputRef="jewel-width_bottom"
			:id="`${prefix}measurements-jewel-width_bottom`"
			:name="`${prefix}measurements[dimensions][width_bottom]`"
			unit="cm"
			:label="dimensionLabel('width_bottom')"
			:instructions="isCutout ? '' : dimensionMaxLabel('width_bottom')"
			:invalid="!isDimensionValid(`${prefix}jewel-width_bottom`)"
			:onBlur="() => addSizeToDataLayer('width_bottom', +widthBottom, 'jewel')"
			v-model="widthBottom"
		/>
		<div class="full" v-if="message('width_top', 'width_bottom', isCutout)">
			<validation-message type="notice">
				{{ message('width_top', 'width_bottom', isCutout) }}
			</validation-message>
		</div>
	</shape-input-fields-wrapper>
</template>
<style scoped lang="scss">
.full {
	grid-column: 1 / -1;
}
</style>
